import type { ReactNode } from "react";
import { classNames } from "../styles/classNames";

export interface CardOption<V extends string> {
  readonly value: V;
  readonly label: string;
}

interface OptionCardProps<V extends string> {
  label: string;
  value: V;
  options: readonly CardOption<V>[];
  onChange: (value: V) => void;
  /** A small symbol before the label. */
  symbol?: ReactNode;
  /** One line on hover: what the setting changes. */
  title?: string;
  disabled?: boolean;
  /** Without it the label is for screen readers only, as on the sign-in page. */
  showLabel?: boolean;
  labelClassName?: string;
  selectClassName?: string;
}

/**
 * One setting as a short card: its name above a choice of a few options (F71), as language
 * and theme have always been shown. Cards sit in an `app-option-cards` group.
 */
export default function OptionCard<V extends string>({
  label,
  value,
  options,
  onChange,
  symbol,
  title,
  disabled = false,
  showLabel = true,
  labelClassName = "app-text-muted app-option-card-label text-xs font-medium",
  selectClassName = "app-input app-select app-option-card-select w-full",
}: OptionCardProps<V>) {
  return (
    <label className="app-option-card app-card-muted" title={title}>
      <span className="app-option-card-meta">
        {symbol}
        <span className={classNames(showLabel ? labelClassName : "sr-only")}>{label}</span>
      </span>
      <select
        value={value}
        disabled={disabled}
        onChange={(event) => {
          const chosen = options.find((option) => option.value === event.target.value);
          if (chosen !== undefined) onChange(chosen.value);
        }}
        className={selectClassName}
        aria-label={label}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}
